import {
  AfterViewInit,
  Component,
  computed,
  ContentChild,
  ElementRef,
  EventEmitter,
  input,
  Input,
  InputSignal,
  Optional,
  Output,
  Signal,
  viewChild,
} from '@angular/core';
import { ControlContainer, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { BaseFormDirective } from '../directives/base-form.directive';
import { TsAutocompleteComponent } from '../ts-autocomplete';
import { HTMLInputTypeAttribute } from 'react';
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { getCurrencySymbol, KeyValuePipe, NgClass, NgForOf, NgIf, NgStyle } from '@angular/common';
import { TSIconModule } from '../ts-icon';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { CurrencyMaskConfig, CurrencyMaskModule } from 'ng2-currency-mask';

export type TsInputFormatType = 'money';

// Formatting types are rendered on a native input of this type; the template adds each type's behaviour
// (a formatting directive).
const FORMAT_TYPE_NATIVE_INPUT: Record<TsInputFormatType, HTMLInputTypeAttribute> = {
  money: 'text',
};

const DEFAULT_CURRENCY_CODE = 'USD';
const DEFAULT_CURRENCY_PRECISION = 2;
const FALLBACK_CURRENCY_SYMBOL = '$';

@Component({
  selector: 'ts-input',
  standalone: true,
  templateUrl: './ts-input.component.html',
  styleUrls: ['./ts-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TsInputComponent,
      multi: true,
    },
    provideNgxMask(),
  ],
  imports: [
    MatAutocompleteModule,
    MatTooltipModule,
    ReactiveFormsModule,
    NgStyle,
    TSIconModule,
    NgClass,
    NgIf,
    NgForOf,
    KeyValuePipe,
    NgxMaskDirective,
    CurrencyMaskModule,
  ],
})
export class TsInputComponent extends BaseFormDirective implements AfterViewInit {
  @Input() label: string;
  @Input() hint: string;
  @Input() placeholder: string = '';
  @Input() icon: string;
  @Input() autofocus = false;
  @Input() clearable = false;
  @Input() maxlength: number;
  @Input() min: number;
  @Input() max: number;
  @Input() backgroundColor: 'white' | 'transparent' = 'transparent';
  @Input() mask: string;
  @Input() blurred = false;

  // Only used when type is 'money'. Accept null/undefined because consumers bind data that may not be loaded yet.
  readonly currencyCode: InputSignal<string | null | undefined> = input<string | null | undefined>(
    DEFAULT_CURRENCY_CODE,
  );
  readonly precision: InputSignal<number | null | undefined> = input<number | null | undefined>(
    DEFAULT_CURRENCY_PRECISION,
  );

  readonly currencyMaskOptions: Signal<Partial<CurrencyMaskConfig>> = computed(() => ({
    align: 'left',
    precision: this.precision() ?? DEFAULT_CURRENCY_PRECISION,
    prefix: getCurrencySymbol(this.currencyCode() || DEFAULT_CURRENCY_CODE, 'wide') || FALLBACK_CURRENCY_SYMBOL,
  }));

  @Input() set type(val: HTMLInputTypeAttribute | TsInputFormatType) {
    this.formatType = isFormatType(val) ? val : null;
    this.inputType = this.formatType ? FORMAT_TYPE_NATIVE_INPUT[this.formatType] : val;
  }

  @Input() tabindex = 0;
  @Input() dataCy: string;
  @Input() errorMessages: { [validationErrorKey: string]: string };
  @Input() readonly = false;
  @Input() variant: 'standard' | 'slim' = 'standard';
  @Input() size: 'md' | 'lg' | 'sm' | 'ss' = 'md';
  @Input() autocomplete = 'off';

  @Output() blur = new EventEmitter<void>();

  @ContentChild(TsAutocompleteComponent) autoCompleteComponentRef: TsAutocompleteComponent;
  inputElement: Signal<ElementRef<HTMLInputElement> | undefined> = viewChild('inputElement');
  matAutocompleteComponentRef: MatAutocomplete;

  inputType: HTMLInputTypeAttribute = 'text';
  formatType: TsInputFormatType | null = null;
  isLabelTruncated = false;

  constructor(@Optional() protected controlContainer: ControlContainer) {
    super(controlContainer);
  }

  ngAfterViewInit(): void {
    if (this.autoCompleteComponentRef) {
      this.matAutocompleteComponentRef = this.autoCompleteComponentRef.autocomplete;
    }

    this.focusInputIfNeeded();
  }

  onInputBlur(): void {
    this.blur.emit();
    this.notifyStandaloneTouched();
  }

  // A money input's value is numeric, so it clears to null; '' would be formatted by the currency mask as 0.00.
  clear(): void {
    this.control.setValue(this.formatType === 'money' ? null : '');
  }

  checkLabelTruncation(labelElement: HTMLElement): void {
    this.isLabelTruncated = labelElement.scrollWidth > labelElement.clientWidth;
  }

  private focusInputIfNeeded(): void {
    if (!this.autoFocusEnabled()) {
      return;
    }

    setTimeout(() => this.inputElement()?.nativeElement.focus(), 0);
  }

  private autoFocusEnabled(): boolean {
    return this.autofocus && !this.readonly;
  }
}

function isFormatType(val: string): val is TsInputFormatType {
  return Object.prototype.hasOwnProperty.call(FORMAT_TYPE_NATIVE_INPUT, val);
}
